import { useEffect, useState, useCallback } from "react";
import { Link, Navigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/lib/i18n";
import LanguageToggle from "@/components/LanguageToggle";
import Logo from "@/components/Logo";
import PlansAdmin from "@/pages/PlansAdmin";
import TicketsAdmin from "@/pages/TicketsAdmin";
import { ArrowLeft, Search, ShieldCheck, Clock, Gift, Ban, Mail as MailIcon, LayoutDashboard, CreditCard, Users, MessageSquare, Send, Receipt, Heart, Activity, Palette, Star, HandCoins, KeyRound, X } from "lucide-react";
import AdminEventsPanel from "@/components/AdminEventsPanel";
import AdminOverviewEventsWidget from "@/components/AdminOverviewEventsWidget";
import AdminInsightsWidget from "@/components/AdminInsightsWidget";
import AffiliatePayoutsAdmin from "@/components/AffiliatePayoutsAdmin";
import AdminInviteTrackingWidget from "@/components/AdminInviteTrackingWidget";
import AdminCashflowGapWidget from "@/components/AdminCashflowGapWidget";
import AdminActivationFunnelWidget from "@/components/AdminActivationFunnelWidget";
import AdminOnboardingFunnelWidget from "@/components/AdminOnboardingFunnelWidget";
import AdminJSearchQuotaWidget from "@/components/AdminJSearchQuotaWidget";
import AdminTourStatsWidget from "@/components/AdminTourStatsWidget";
import ThinkingLoader from "@/components/ThinkingLoader";
import BrandingAdmin from "@/components/BrandingAdmin";
import SecurityAdmin from "@/components/SecurityAdmin";
import TestimonialsAdmin from "@/components/TestimonialsAdmin";
import { toast } from "sonner";

const STATUS_FILTERS = ["all", "trial", "active", "expired", "admin"];

function StatusBadge({ u, t }) {
  if (u.is_admin) {
    return <span className="ed-badge bg-neutral-900 text-[#F9F8F6]" data-testid={`badge-admin-${u.user_id}`}>{t("admLabelAdmin")}</span>;
  }
  if (u.billing_exempt || u.subscription_status === "friends_family") {
    return (
      <span className="ed-badge bg-rose-100 text-rose-800 border border-rose-300 inline-flex items-center gap-1" data-testid={`badge-ff-${u.user_id}`}>
        <Heart size={9} strokeWidth={2} /> {t("admLabelFriendsFamily")}
      </span>
    );
  }
  const map = {
    trial: ["badge-applied", t("admLabelTrial")],
    active: ["badge-invited", t("admLabelActive")],
    expired: ["badge-rejected", t("admLabelExpired")],
  };
  const [cls, label] = map[u.subscription_status] || map.trial;
  return <span className={`ed-badge ${cls}`}>{label}</span>;
}

function fmtDate(iso, lang) {
  if (!iso || typeof iso !== "string") return "—";
  try {
    return new Date(iso).toLocaleDateString(lang === "en" ? "en-GB" : "nl-NL");
  } catch {
    return "—";
  }
}

// Defensive helper — legacy DB records occasionally store `{}` where a
// string is expected. React can't render objects as children (error #31)
// and the whole tab blanks. Fall back to em-dash for anything non-scalar.
const safeText = (v) => (typeof v === "string" || typeof v === "number" ? v : "—");

export default function Admin() {
  const { t, lang } = useT();
  const { user, loading } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState(null);
  const [txs, setTxs] = useState([]);
  const [emails, setEmails] = useState([]);
  const [emailBusy, setEmailBusy] = useState(null);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [busyUid, setBusyUid] = useState(null);
  const [section, setSection] = useState("overview");
  const [notifCounts, setNotifCounts] = useState({ pending_testimonials: 0, open_tickets: 0, failed_emails: 0, total: 0 });
  // Signup drill-down modal — either null (closed) or an ISO date string
  // ("2026-02-15") or "all" for the entire 7-day rolling window.
  const [signupDrill, setSignupDrill] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, u, tx, em] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users", { params: { status, q } }),
        api.get("/admin/transactions"),
        api.get("/admin/emails"),
      ]);
      setStats(s.data);
      // Backend was paginated to `{items, total, page, page_size, pages}`.
      // Extract `items` when present so downstream `.map`/`.filter` calls
      // keep working on the flat array shape they expect.
      setUsers(Array.isArray(u.data) ? u.data : (u.data?.items || []));
      setTxs(tx.data);
      setEmails(em.data);
    } catch {
      toast.error(t("admActionFailed"));
    }
  }, [status, q, t]);

  useEffect(() => {
    if (user?.is_admin) load();
  }, [user, load]);

  // Poll notification counts (pending testimonials, open tickets, failed emails)
  // every 60s so the sidebar badges stay fresh without a hard refresh.
  useEffect(() => {
    if (!user?.is_admin) return;
    let cancelled = false;
    const fetchCounts = async () => {
      try {
        const r = await api.get("/admin/notifications/counts");
        if (!cancelled) setNotifCounts(r.data || { pending_testimonials: 0, open_tickets: 0, failed_emails: 0, total: 0 });
      } catch { /* silent — badges just stay stale */ }
    };
    fetchCounts();
    const iv = setInterval(fetchCounts, 60000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [user]);

  if (loading) return null;
  if (!user) return <Navigate to="/admin/login" replace />;
  if (!user.is_admin) {
    return (
      <div className="ed-page min-h-screen flex items-center justify-center">
        <div className="ed-card p-14 text-center" data-testid="admin-forbidden">
          <p className="font-serif text-2xl">{t("admForbidden")}</p>
          <div className="mt-8 flex justify-center gap-3">
            <Link to="/dashboard" className="ed-btn inline-flex">{t("admBackToApp")}</Link>
            <Link to="/admin/login" className="ed-btn ed-btn-outline inline-flex">Admin login</Link>
          </div>
        </div>
      </div>
    );
  }

  const act = async (uid, path, body) => {
    setBusyUid(uid);
    try {
      await api.post(`/admin/users/${uid}/${path}`, body || {});
      toast.success(t("admActionOk"));
      load();
    } catch {
      toast.error(t("admActionFailed"));
    } finally {
      setBusyUid(null);
    }
  };

  const resetUserPassword = async (uid, email) => {
    // Simple browser prompt — the admin is on their own machine so shoulder-
    // surf risk is minimal, and it avoids introducing a bespoke modal.
    const pwd = window.prompt(t("admActResetPrompt", { email }), "");
    if (pwd === null) return;  // cancelled
    const trimmed = pwd.trim();
    if (trimmed.length < 8 || !/[A-Za-z]/.test(trimmed) || !/[0-9]/.test(trimmed)) {
      toast.error(t("admActResetInvalid"));
      return;
    }
    setBusyUid(uid);
    try {
      await api.post(`/admin/users/${uid}/reset-password`, { new_password: trimmed });
      toast.success(t("admActResetOk", { email }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("admActionFailed"));
    } finally {
      setBusyUid(null);
    }
  };

  const sendTestEmail = async (kind) => {
    setEmailBusy(kind);
    try {
      await api.post("/admin/emails/test", { kind });
      toast.success(t("admEmailSent"));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("admActionFailed"));
    } finally {
      setEmailBusy(null);
    }
  };

  const sendWeeklyDigestPreview = async () => {
    setEmailBusy("weekly_digest");
    try {
      const r = await api.post("/admin/digest/weekly-test");
      const sent = r?.data?.sent ?? 0;
      toast.success(t("admDigestWeeklySent", { n: sent }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("admActionFailed"));
    } finally {
      setEmailBusy(null);
    }
  };

  const statCards = stats ? [
    ["admStatUsers", stats.total_users],
    ["admStatTrial", stats.trial],
    ["admStatActive", stats.active],
    ["admStatExpired", stats.expired],
    ["admStatNew7", stats.new_last_7d],
    ["admStatRevenue", `€${stats.revenue_total.toFixed(2)}`],
    ["admStatRevenue30", `€${stats.revenue_30d.toFixed(2)}`],
  ] : [];

  return (
    <div className="ed-page min-h-screen" data-testid="admin-page">
      <header className="ed-header px-8 py-5 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Logo size="md" showTagline={false} href="/dashboard" />
          <span className="hidden sm:inline-block h-6 w-px bg-neutral-300" />
          <span className="font-mono text-[0.65rem] uppercase tracking-[0.25em] text-neutral-500 flex items-center gap-2">
            <ShieldCheck size={12} strokeWidth={1.5} /> {t("admTitle")}
          </span>
        </div>
        <div className="flex items-center gap-4">
          <LanguageToggle testId="admin-language-toggle" />
          <Link to="/dashboard" className="ed-btn ed-btn-outline" data-testid="admin-back">
            <ArrowLeft size={14} strokeWidth={1.5} /> <span className="hidden sm:inline">{t("admBackToApp")}</span>
          </Link>
        </div>
      </header>

      <div className="flex flex-col lg:flex-row">
        {/* Sidebar */}
        <aside className="lg:w-64 lg:sticky lg:top-0 lg:h-[calc(100vh-4rem)] border-b lg:border-b-0 lg:border-r border-neutral-200 bg-white/60 backdrop-blur-sm" data-testid="admin-sidebar">
          <nav className="flex lg:flex-col overflow-x-auto lg:overflow-visible p-2 lg:p-4 gap-1">
            {[
              ["overview", LayoutDashboard, t("admNavOverview"), 0],
              ["plans", CreditCard, t("admNavPlans"), 0],
              ["users", Users, t("admNavUsers"), 0],
              ["tickets", MessageSquare, t("admNavTickets"), notifCounts.open_tickets],
              ["emails", Send, t("admNavEmails"), notifCounts.failed_emails],
              ["testimonials", Star, t("admNavTestimonials"), notifCounts.pending_testimonials],
              ["affiliates", HandCoins, t("admNavAffiliates"), 0],
              ["transactions", Receipt, t("admNavTransactions"), 0],
              ["events", Activity, t("admNavEvents"), 0],
              ["branding", Palette, t("admNavBranding"), 0],
              ["security", ShieldCheck, t("admNavSecurity"), user?.two_fa?.enabled ? 0 : 1],
            ].map(([key, Icon, label, badge]) => (
              <button
                key={key}
                data-testid={`admin-nav-${key}`}
                onClick={() => setSection(key)}
                className={`w-full inline-flex items-center gap-3 px-4 py-3 font-mono text-[0.65rem] uppercase tracking-[0.15em] transition-colors text-left whitespace-nowrap ${
                  section === key ? "bg-neutral-900 text-[#F9F8F6]" : "text-neutral-600 hover:bg-neutral-100"
                }`}
              >
                <Icon size={13} strokeWidth={1.5} />
                <span className="flex-1">{label}</span>
                {badge > 0 && (
                  <span
                    data-testid={`admin-nav-badge-${key}`}
                    className={`inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-[0.6rem] font-semibold tracking-normal ${
                      section === key ? "bg-[#F9F8F6] text-neutral-900" : "bg-rose-600 text-white"
                    }`}
                    title={`${badge} ${label}`}
                  >
                    {badge > 99 ? "99+" : badge}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </aside>

      <main className="flex-1 min-w-0 max-w-6xl mx-auto px-6 sm:px-8 py-8 lg:py-12 w-full">
        {section === "overview" && (
          <>
            <p className="font-serif text-2xl italic text-neutral-800">{t("admSubtitle")}</p>
            <AdminInsightsWidget />
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-4 gap-3 mt-8" data-testid="admin-stats">
              {statCards.map(([key, val]) => (
                <div key={key} className="ed-card p-4">
                  <p className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500">{t(key)}</p>
                  <p className="font-serif text-3xl mt-2" data-testid={`stat-${key}`}>{val}</p>
                </div>
              ))}
            </div>

            {/* Per-day signup breakdown — 7-day rolling window bar chart */}
            {stats?.signups_per_day && (
              <div className="ed-card p-6 mt-4" data-testid="admin-signups-per-day">
                <div className="flex items-baseline justify-between gap-3 mb-4 flex-wrap">
                  <div>
                    <p className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500">
                      Nieuwe aanmeldingen — laatste 7 dagen
                    </p>
                    <button
                      type="button"
                      data-testid="stat-signups-total"
                      onClick={() => setSignupDrill("all")}
                      className="font-serif text-2xl mt-1 hover:underline underline-offset-4 decoration-neutral-400 cursor-pointer text-left"
                      title="Bekijk alle aangemelde gebruikers"
                    >
                      {stats.signups_per_day.reduce((a, d) => a + d.count, 0)} aanmeldingen ›
                    </button>
                  </div>
                  <p className="font-mono text-[0.6rem] text-neutral-500">
                    piek: {Math.max(...stats.signups_per_day.map(d => d.count))} / dag
                    {(() => {
                      const hrs = stats.signups_hours_7d || [];
                      if (!hrs.length || !hrs.some(v => v > 0)) return null;
                      const max = Math.max(...hrs);
                      const peakHour = hrs.indexOf(max);
                      return (
                        <> · beste uur:
                          <span className="ml-1 text-neutral-900" data-testid="stat-peak-hour-7d">
                            {String(peakHour).padStart(2, "0")}:00 – {String((peakHour + 1) % 24).padStart(2, "0")}:00 UTC
                          </span>
                        </>
                      );
                    })()}
                  </p>
                </div>
                {(() => {
                  const days = stats.signups_per_day;
                  const max = Math.max(1, ...days.map((d) => d.count));
                  const dayNames = ["Zo","Ma","Di","Wo","Do","Vr","Za"];
                  return (
                    <div className="flex items-end gap-2 h-40">
                      {days.map((d) => {
                        const pct = Math.max(4, Math.round((d.count / max) * 100));
                        const date = new Date(d.date + "T00:00:00Z");
                        const dayName = dayNames[date.getUTCDay()];
                        const dayNum = date.getUTCDate();
                        const peakLabel = (d.peak_hour !== null && d.peak_hour !== undefined && d.count > 0)
                          ? `${String(d.peak_hour).padStart(2, "0")}h`
                          : "—";
                        return (
                          <div key={d.date} className="flex-1 flex flex-col items-center gap-2 min-w-0" data-testid={`signup-bar-${d.date}`}>
                            <div className="w-full flex-1 flex flex-col justify-end">
                              <button
                                type="button"
                                onClick={() => d.count > 0 && setSignupDrill(d.date)}
                                disabled={d.count === 0}
                                className={`w-full bg-neutral-900 hover:bg-neutral-700 transition-colors relative disabled:cursor-default disabled:hover:bg-neutral-900 ${d.count > 0 ? "cursor-pointer" : ""}`}
                                style={{ height: `${pct}%`, minHeight: "4px" }}
                                title={d.count > 0 ? `${d.date}: ${d.count} aanmeldingen · piek ${peakLabel} · klik voor lijst` : `${d.date}: 0 aanmeldingen`}
                                data-testid={`signup-bar-btn-${d.date}`}
                              >
                                {d.count > 0 && (
                                  <span className="absolute -top-6 left-1/2 -translate-x-1/2 font-mono text-[0.65rem] text-neutral-700">
                                    {d.count}
                                  </span>
                                )}
                              </button>
                            </div>
                            <div className="text-center">
                              <p className="font-mono text-[0.55rem] uppercase tracking-[0.1em] text-neutral-500">{dayName}</p>
                              <p className="font-serif text-xs text-neutral-700">{dayNum}</p>
                              <p
                                className="font-mono text-[0.55rem] text-neutral-500 mt-0.5"
                                data-testid={`signup-peakhour-${d.date}`}
                                title="Piek-uur (UTC) van deze dag"
                              >
                                {peakLabel}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}

                {/* 24-hour heatstrip aggregated over the 7-day window.
                    Reveals when to schedule ads for best conversion. */}
                {stats.signups_hours_7d && stats.signups_hours_7d.some(v => v > 0) && (
                  <div className="mt-6 pt-5 border-t border-neutral-200" data-testid="admin-signups-hour-heat">
                    <p className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500 mb-2">
                      Verdeling over de dag (UTC · alle 7 dagen samen)
                    </p>
                    {(() => {
                      const hrs = stats.signups_hours_7d;
                      const mx = Math.max(1, ...hrs);
                      return (
                        <>
                          <div className="flex gap-[2px] items-end h-14">
                            {hrs.map((c, h) => {
                              const heightPct = c > 0 ? Math.max(8, Math.round((c / mx) * 100)) : 0;
                              const intensity = c === 0
                                ? "bg-neutral-100"
                                : (c === mx ? "bg-amber-500" : "bg-neutral-800");
                              return (
                                <div
                                  key={h}
                                  className="flex-1 flex flex-col justify-end min-w-0"
                                  data-testid={`signup-hour-${h}`}
                                  title={`${String(h).padStart(2, "0")}:00 UTC — ${c} aanmelding(en)`}
                                >
                                  <div
                                    className={`w-full ${intensity} transition-colors`}
                                    style={{ height: `${heightPct}%`, minHeight: c > 0 ? "3px" : "0" }}
                                  />
                                </div>
                              );
                            })}
                          </div>
                          <div className="flex justify-between mt-1 font-mono text-[0.55rem] text-neutral-500">
                            <span>00h</span><span>06h</span><span>12h</span><span>18h</span><span>23h</span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}

            {/* 7-day activation funnel: signup → verified → CV → paid */}
            <AdminActivationFunnelWidget funnel={stats?.funnel_7d} />

            {/* Onboarding tour per-step drop-off */}
            <AdminOnboardingFunnelWidget />

            {/* RapidAPI JSearch quota monitor */}
            <AdminJSearchQuotaWidget />

            {/* Product tour engagement — opens, completes, tab popularity */}
            <AdminTourStatsWidget />

            <AdminOverviewEventsWidget onOpenAll={() => setSection("events")} />
          </>
        )}

        {section === "plans" && <PlansAdmin />}
        {section === "tickets" && <TicketsAdmin />}
        {section === "events" && <AdminEventsPanel />}
        {section === "branding" && <BrandingAdmin />}
        {section === "security" && <SecurityAdmin />}
        {section === "testimonials" && (
          <TestimonialsAdmin onChanged={async () => {
            try {
              const r = await api.get("/admin/notifications/counts");
              setNotifCounts(r.data || { pending_testimonials: 0, open_tickets: 0, failed_emails: 0, total: 0 });
            } catch { /* silent */ }
          }} />
        )}
        {section === "affiliates" && (
          <div className="space-y-6">
            <AdminCashflowGapWidget />
            <AffiliatePayoutsAdmin />
            <AdminInviteTrackingWidget />
          </div>
        )}

        {section === "users" && (
          <div data-testid="admin-users-section">
            <p className="ed-eyebrow mb-4">{t("admNavUsers")}</p>
            <div className="flex flex-wrap items-center gap-3 mb-5">
          <div className="flex border border-neutral-300">
            {STATUS_FILTERS.map((s) => (
              <button
                key={s}
                data-testid={`admin-filter-${s}`}
                onClick={() => setStatus(s)}
                className={`px-4 py-2 font-mono text-[0.65rem] uppercase tracking-[0.15em] transition-colors ${
                  status === s ? "bg-neutral-900 text-[#F9F8F6]" : "hover:bg-neutral-100"
                }`}
              >
                {t(`admFilter${s.charAt(0).toUpperCase() + s.slice(1)}`)}
              </button>
            ))}
          </div>
          <div className="relative flex-1 min-w-[16rem] max-w-sm">
            <Search size={14} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input
              data-testid="admin-search"
              className="ed-input pl-9"
              placeholder={t("admSearchPh")}
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
        </div>

        {/* Users table */}
        <div className="ed-card overflow-x-auto">
          <table className="ed-table">
            <thead>
              <tr>
                <th>{t("admColUser")}</th>
                <th>{t("admColStatus")}</th>
                <th>{t("admColPlan")}</th>
                <th>{t("admColTrialEnds")}</th>
                <th>{t("admColSubEnds")}</th>
                <th>{t("admColSince")}</th>
                <th className="text-right">{t("admColActions")}</th>
              </tr>
            </thead>
            <tbody>
              {users === null ? (
                <tr><td colSpan={7} className="text-center py-8"><span className="ed-eyebrow">{t("loading")}</span></td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-8 font-serif italic text-neutral-500" data-testid="admin-no-users">{t("admNoUsers")}</td></tr>
              ) : users.map((u, i) => (
                <tr key={u.user_id} data-testid={`admin-user-${i}`}>
                  <td>
                    <p className="font-serif text-lg leading-tight">{safeText(u.name) === "—" ? "—" : u.name}</p>
                    <p className="font-mono text-xs text-neutral-500">{safeText(u.email)}</p>
                  </td>
                  <td>
                    <StatusBadge u={u} t={t} />
                    {!u.is_admin && u.subscription_status !== "expired" && u.subscription_status !== "friends_family" && (
                      <p className="font-mono text-[0.6rem] text-neutral-500 mt-1">{t("admDaysLeft", { days: u.days_left })}</p>
                    )}
                  </td>
                  <td className="font-mono text-xs uppercase">{safeText(u.subscription_plan) === "—" ? "—" : u.subscription_plan}</td>
                  <td className="font-mono text-xs">{fmtDate(u.trial_ends_at, lang)}</td>
                  <td className="font-mono text-xs">{fmtDate(u.subscription_expires_at, lang)}</td>
                  <td className="font-mono text-xs">{fmtDate(u.created_at, lang)}</td>
                  <td>
                    <div className="flex justify-end flex-wrap gap-1.5">
                      <button data-testid={`admin-extend-${i}`} disabled={busyUid === u.user_id}
                        onClick={() => act(u.user_id, "extend-trial", { days: 7 })}
                        className="px-2.5 py-1.5 border border-neutral-300 font-mono text-[0.6rem] uppercase tracking-wider hover:border-neutral-900 transition-colors inline-flex items-center gap-1">
                        <Clock size={11} strokeWidth={1.5} /> {t("admActExtend")}
                      </button>
                      <button data-testid={`admin-grant30-${i}`} disabled={busyUid === u.user_id}
                        onClick={() => act(u.user_id, "grant", { days: 30 })}
                        className="px-2.5 py-1.5 border border-neutral-300 font-mono text-[0.6rem] uppercase tracking-wider hover:border-neutral-900 transition-colors inline-flex items-center gap-1">
                        <Gift size={11} strokeWidth={1.5} /> {t("admActGrant30")}
                      </button>
                      <button data-testid={`admin-grant365-${i}`} disabled={busyUid === u.user_id}
                        onClick={() => act(u.user_id, "grant", { days: 365 })}
                        className="px-2.5 py-1.5 border border-neutral-300 font-mono text-[0.6rem] uppercase tracking-wider hover:border-neutral-900 transition-colors inline-flex items-center gap-1">
                        <Gift size={11} strokeWidth={1.5} /> {t("admActGrant365")}
                      </button>
                      <button data-testid={`admin-revoke-${i}`} disabled={busyUid === u.user_id}
                        onClick={() => act(u.user_id, "revoke")}
                        className="px-2.5 py-1.5 border border-neutral-300 font-mono text-[0.6rem] uppercase tracking-wider hover:border-rose-700 hover:text-rose-700 transition-colors inline-flex items-center gap-1">
                        <Ban size={11} strokeWidth={1.5} /> {t("admActRevoke")}
                      </button>
                      <button data-testid={`admin-toggleadmin-${i}`} disabled={busyUid === u.user_id}
                        onClick={() => act(u.user_id, "toggle-admin")}
                        className="px-2.5 py-1.5 border border-neutral-300 font-mono text-[0.6rem] uppercase tracking-wider hover:border-neutral-900 transition-colors inline-flex items-center gap-1">
                        <ShieldCheck size={11} strokeWidth={1.5} /> {t("admActAdmin")}
                      </button>
                      <button data-testid={`admin-togglebilling-${i}`} disabled={busyUid === u.user_id}
                        onClick={() => act(u.user_id, "toggle-billing-exempt")}
                        className={`px-2.5 py-1.5 border font-mono text-[0.6rem] uppercase tracking-wider transition-colors inline-flex items-center gap-1 ${u.billing_exempt ? "border-rose-500 text-rose-700 bg-rose-50" : "border-neutral-300 hover:border-rose-500 hover:text-rose-700"}`}
                        title={t("admActBillingExemptHint")}>
                        <Heart size={11} strokeWidth={1.5} /> {u.billing_exempt ? t("admActBillingExemptOn") : t("admActBillingExemptOff")}
                      </button>
                      <button data-testid={`admin-resetpwd-${i}`} disabled={busyUid === u.user_id}
                        onClick={() => resetUserPassword(u.user_id, u.email)}
                        className="px-2.5 py-1.5 border border-neutral-300 font-mono text-[0.6rem] uppercase tracking-wider hover:border-amber-600 hover:text-amber-700 transition-colors inline-flex items-center gap-1"
                        title={t("admActResetHint")}>
                        <KeyRound size={11} strokeWidth={1.5} /> {t("admActReset")}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
          </div>
        )}

        {section === "emails" && (
          <div data-testid="admin-emails-section">
        {/* Emails */}
        <p className="ed-eyebrow mt-2 mb-4">{t("admEmails")}</p>

        <DormantThresholdCard t={t} />

        <div className="ed-card p-5 mb-5" data-testid="admin-emails-panel">
          <p className="text-xs font-mono text-neutral-500 mb-4">{t("admEmailNote")}</p>
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-serif italic text-neutral-700">{t("admEmailTestHint")}</span>
            {[["welcome", "admEmailKindWelcome"], ["trial_reminder", "admEmailKindReminder"], ["trial_expired", "admEmailKindExpired"], ["followup_digest", "admEmailKindFollowup"], ["pack_purchased", "admEmailKindPackPurchased"], ["rebuy_reminder", "admEmailKindRebuyReminder"], ["affiliate_payout", "admEmailKindAffiliatePayout"], ["dormant_partner", "admEmailKindDormantPartner"]].map(([kind, labelKey]) => (
              <button
                key={kind}
                data-testid={`admin-email-test-${kind}`}
                onClick={() => sendTestEmail(kind)}
                disabled={!!emailBusy}
                className="px-3 py-2 border border-neutral-300 font-mono text-[0.65rem] uppercase tracking-wider hover:border-neutral-900 transition-colors inline-flex items-center gap-1.5 disabled:opacity-60"
              >
                <MailIcon size={12} strokeWidth={1.5} /> {emailBusy === kind ? "…" : t(labelKey)}
              </button>
            ))}
            <button
              data-testid="admin-email-test-weekly-digest"
              onClick={sendWeeklyDigestPreview}
              disabled={!!emailBusy}
              className="px-3 py-2 border border-neutral-900 bg-neutral-900 text-[#F9F8F6] font-mono text-[0.65rem] uppercase tracking-wider hover:bg-neutral-700 transition-colors inline-flex items-center gap-1.5 disabled:opacity-60"
              title={t("admDigestWeeklyHint")}
            >
              <MailIcon size={12} strokeWidth={1.5} /> {emailBusy === "weekly_digest" ? "…" : t("admDigestWeeklyCta")}
            </button>
          </div>
        </div>
        <div className="ed-card overflow-x-auto">
          <table className="ed-table">
            <thead>
              <tr>
                <th>{t("ltCreatedAt")}</th>
                <th>{t("admColUser")}</th>
                <th>{t("admEmailColKind")}</th>
                <th>{t("admColStatus")}</th>
              </tr>
            </thead>
            <tbody>
              {emails.length === 0 ? (
                <tr><td colSpan={4} className="text-center py-8 font-serif italic text-neutral-500" data-testid="admin-no-emails">{t("admEmailEmpty")}</td></tr>
              ) : emails.map((em, i) => (
                <tr key={em.id || i} data-testid={`admin-email-${i}`}>
                  <td className="font-mono text-xs">{fmtDate(em.created_at, lang)}</td>
                  <td className="font-mono text-xs">{em.email}</td>
                  <td className="font-mono text-xs uppercase">{em.kind}{em.is_test ? " (test)" : ""}</td>
                  <td>
                    <span className={`ed-badge ${em.status === "sent" ? "badge-invited" : em.status === "failed" ? "badge-rejected" : "badge-applied"}`}>
                      {em.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
          </div>
        )}

        {section === "transactions" && (
          <div data-testid="admin-transactions-section">
        {/* Transactions */}
        <p className="ed-eyebrow mt-2 mb-4">{t("admTransactions")}</p>
        <div className="ed-card overflow-x-auto">
          <table className="ed-table">
            <thead>
              <tr>
                <th>{t("ltCreatedAt")}</th>
                <th>{t("admColUser")}</th>
                <th>{t("admColPlan")}</th>
                <th>EUR</th>
                <th>{t("admColStatus")}</th>
              </tr>
            </thead>
            <tbody>
              {txs.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-8 font-serif italic text-neutral-500" data-testid="admin-no-txs">{t("admTxEmpty")}</td></tr>
              ) : txs.map((tx, i) => (
                <tr key={tx.id || i} data-testid={`admin-tx-${i}`}>
                  <td className="font-mono text-xs">{fmtDate(tx.created_at, lang)}</td>
                  <td className="font-mono text-xs">{tx.email}</td>
                  <td className="font-mono text-xs uppercase">{tx.plan}</td>
                  <td className="font-mono text-xs">€{Number(tx.amount).toFixed(2)}</td>
                  <td>
                    <span className={`ed-badge ${tx.payment_status === "paid" ? "badge-invited" : "badge-applied"}`}>
                      {tx.payment_status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
          </div>
        )}
      </main>
      </div>

      {/* Signup drill-down modal — click on the "X aanmeldingen" total or a
          daily bar to see who signed up in that window. */}
      {signupDrill && (
        <SignupDrillDownModal
          filter={signupDrill}
          onClose={() => setSignupDrill(null)}
          onNavigateUser={(email) => { setSection("users"); setQ(email); setSignupDrill(null); }}
          lang={lang}
          t={t}
        />
      )}
    </div>
  );
}

/**
 * Modal listing the users who signed up in the given window.
 * `filter` is either "all" (last 7 days) or an ISO date "YYYY-MM-DD".
 * Reuses the `/admin/users` endpoint we already load — no extra network call.
 */
function SignupDrillDownModal({ filter, onClose, onNavigateUser, lang, t }) {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [onClose]);

  useEffect(() => {
    let cancel = false;
    api.get("/admin/users", { params: { limit: 500 } }).then((r) => {
      if (cancel) return;
      const all = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      const cutoffIso = (() => {
        const d = new Date();
        d.setUTCHours(0, 0, 0, 0);
        d.setUTCDate(d.getUTCDate() - 6);
        return d.toISOString().slice(0, 10);
      })();
      const filtered = all
        .filter((u) => !u.is_admin)  // admins are excluded from acquisition stats
        .filter((u) => {
          const created = (u.created_at || "").slice(0, 10);
          if (!created) return false;
          if (filter === "all") return created >= cutoffIso;
          return created === filter;
        })
        .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
      setRows(filtered);
    }).catch(() => setRows([]));
    return () => { cancel = true; };
  }, [filter]);

  const title = filter === "all"
    ? "Aanmeldingen · laatste 7 dagen"
    : `Aanmeldingen op ${new Date(filter + "T00:00:00Z").toLocaleDateString(lang === "en" ? "en-GB" : "nl-NL")}`;

  return (
    <>
      <div
        className="fixed inset-0 z-[65] bg-black/50"
        onClick={onClose}
        data-testid="signup-drill-backdrop"
      />
      <div
        role="dialog"
        aria-modal="true"
        data-testid="signup-drill-modal"
        className="fixed z-[66] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(42rem,calc(100vw-2rem))] max-h-[85vh] bg-white border border-neutral-900 shadow-2xl flex flex-col"
      >
        <div className="px-5 py-4 border-b border-neutral-200 flex items-center justify-between shrink-0">
          <div>
            <p className="ed-eyebrow">Nieuwe aanmeldingen</p>
            <p className="font-serif text-lg mt-1" data-testid="signup-drill-title">
              {title}
              {rows && <span className="ml-2 font-mono text-xs text-neutral-500">· {rows.length}</span>}
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="signup-drill-close"
            className="text-neutral-500 hover:text-neutral-900"
            aria-label="Close"
          >
            <X size={14} strokeWidth={1.6} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1">
          {rows === null ? (
            <div className="p-8 flex justify-center" data-testid="signup-drill-loading">
              <ThinkingLoader stage="generic" testId="signup-drill-thinking" />
            </div>
          ) : rows.length === 0 ? (
            <p className="p-8 text-center font-serif italic text-neutral-500" data-testid="signup-drill-empty">
              Geen aanmeldingen in dit venster.
            </p>
          ) : (
            <ol className="divide-y divide-neutral-100">
              {rows.map((u, i) => (
                <li
                  key={u.user_id || i}
                  data-testid={`signup-drill-row-${i}`}
                  className="px-5 py-3 flex items-center gap-4 hover:bg-neutral-50 cursor-pointer"
                  onClick={() => onNavigateUser(u.email)}
                  title="Bekijk deze gebruiker in de Gebruikers-tab"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-serif text-sm leading-tight truncate">
                      {typeof u.name === "string" && u.name ? u.name : "—"}
                    </p>
                    <p className="font-mono text-xs text-neutral-500 truncate">{u.email}</p>
                  </div>
                  <span className={`ed-badge shrink-0 ${
                    u.subscription_status === "active" ? "badge-invited"
                    : u.subscription_status === "trial" ? "badge-applied"
                    : u.subscription_status === "expired" ? "badge-rejected"
                    : "bg-neutral-100 text-neutral-700"
                  }`}>
                    {u.subscription_status || "—"}
                  </span>
                  <span className="font-mono text-[0.65rem] text-neutral-500 shrink-0 tabular-nums">
                    {u.created_at ? new Date(u.created_at).toLocaleString(lang === "en" ? "en-GB" : "nl-NL") : "—"}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </>
  );
}

// Small self-contained admin card for the dormant-partner threshold. Kept
// inline (rather than a new file) because it's ~40 lines and has no other
// call sites — colocation makes the Admin page easier to reason about.
function DormantThresholdCard({ t }) {
  const [days, setDays] = useState(30);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/admin/emails/dormant-threshold")
      .then((r) => { setDays(r.data?.days ?? 30); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/emails/dormant-threshold", { days: Math.max(7, Math.min(120, Number(days) || 30)) });
      toast.success(t("admDormantSaved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("admDormantSaveFailed"));
    } finally { setBusy(false); }
  };

  return (
    <div className="ed-card p-5 mb-5" data-testid="admin-dormant-threshold-card">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <p className="ed-eyebrow">{t("admDormantEyebrow")}</p>
          <p className="font-serif text-lg mt-2 leading-tight">{t("admDormantTitle")}</p>
          <p className="text-xs text-neutral-600 mt-2 leading-relaxed max-w-lg">{t("admDormantHelp")}</p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="number"
            min={7}
            max={120}
            step={1}
            data-testid="admin-dormant-days-input"
            value={days}
            onChange={(e) => setDays(Number(e.target.value) || 0)}
            disabled={!loaded}
            className="w-20 border border-neutral-300 px-3 py-2 font-mono text-sm text-center focus:border-neutral-900 outline-none"
          />
          <span className="font-mono text-xs text-neutral-500">{t("admDormantUnit")}</span>
          <button
            onClick={save}
            data-testid="admin-dormant-save"
            disabled={busy || !loaded}
            className="ed-btn ml-2 disabled:opacity-50"
          >
            {busy ? "…" : t("admDormantSave")}
          </button>
        </div>
      </div>
    </div>
  );
}
